import i18n from "../i18n";

/**
 * Format a monetary amount to exactly two decimals with locale grouping.
 *
 * The API serialises NUMERIC(18,4) money columns as JSON strings with four
 * trailing decimals (e.g. "50000.0000"). The frontend type claims `number`,
 * so a naive `value.toLocaleString({...})` runs on a string and silently
 * ignores the fraction-digit options, leaking "50000.0000" to the UI. This
 * helper coerces to a number first so the formatting actually applies.
 *
 * @returns the formatted string (e.g. "50,000.00"), or `null` for
 *          empty/non-numeric input so callers can render a muted "—".
 */
export function formatAmount2(
  value: string | number | null | undefined,
): string | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return n.toLocaleString(i18n.language || undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Coerce a money value to a clean string for a `<input type="number">`.
 *
 * Strips the four trailing zeros the API sends ("50000.0000" → "50000") while
 * preserving real cents ("1234.5600" → "1234.56"). Returns "" for empty input.
 * Number inputs cannot contain locale grouping, so this never adds separators.
 */
export function toAmountInput(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  return String(n);
}
